import { useState } from "react";
import { Empty, Table, Tag, Select, Input, Button, Dropdown, Modal } from "antd";
import {
  PlusOutlined,
  ReloadOutlined,
  SearchOutlined,
  MoreOutlined,
  EyeOutlined,
  EditOutlined,
  DeleteOutlined,
  SwapOutlined,
  DollarOutlined,
} from "@ant-design/icons";
import type { MenuProps } from "antd";
import { RepairJob, RepairJobStatus, RepairJobPriority } from "../../Types/RepairJobTypes";
import {
  useRepairJobQuery,
  useRepairJobDeleteMutation,
  useRepairJobStatusUpdateMutation,
  useSectionAccess,
} from "../../Services";
import { RepairJobAction } from "../../Types";
import { getRepairJobById } from "../../Api";
import RepairJobFormDrawer from "./common/RepairJobFormDrawer";
import RepairJobViewDrawer from "./common/RepairJobViewDrawer";
import RepairJobPaymentModal from "./common/RepairJobPaymentModal";
import { PageHeader, tablePagination } from "../../components";
import { PAGE_SIZE } from "../../Constant";
import { message } from "antd";

const { Option } = Select;

const statusColors: Record<string, string> = {
  pending: "orange",
  diagnosed: "blue",
  in_progress: "cyan",
  completed: "green",
  delivered: "purple",
  cancelled: "red",
};

const priorityColors: Record<string, string> = {
  low: "default",
  normal: "blue",
  high: "orange",
  urgent: "red",
};

export const RepairJobsPage = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedStatus, setSelectedStatus] = useState<RepairJobStatus | "">("");
  const [selectedPriority, setSelectedPriority] = useState<RepairJobPriority | "">("");
  const [page, setPage] = useState(1);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [viewDrawerOpen, setViewDrawerOpen] = useState(false);
  const [selectedJob, setSelectedJob] = useState<RepairJob | null>(null);
  const [viewJob, setViewJob] = useState<RepairJob | null>(null);
  const [viewLoading, setViewLoading] = useState(false);
  const [paymentJob, setPaymentJob] = useState<RepairJob | null>(null);

  const filters = {
    page,
    limit: PAGE_SIZE,
    search: searchTerm,
    status: selectedStatus || undefined,
    priority: selectedPriority || undefined,
  };

  const { data: response, isLoading, refetch } = useRepairJobQuery(filters);
  const deleteMutation = useRepairJobDeleteMutation();
  const statusUpdateMutation = useRepairJobStatusUpdateMutation();
  const { isAllowed } = useSectionAccess("repair_jobs");

  const jobs = response?.data ?? [];
  const total = response?.meta?.total ?? 0;

  const openDrawer = (job?: RepairJob) => {
    setSelectedJob(job || null);
    setDrawerOpen(true);
  };

  const closeDrawer = () => {
    setDrawerOpen(false);
    setSelectedJob(null);
  };

  const handleViewJob = async (job: RepairJob) => {
    setViewLoading(true);
    try {
      const res = await getRepairJobById(job.id);
      if (res.success) {
        setViewJob(res.data);
        setViewDrawerOpen(true);
      } else {
        message.error(res?.message || "Failed to load repair job details.");
      }
    } catch {
      message.error("Failed to load repair job details.");
    } finally {
      setViewLoading(false);
    }
  };

  const handleQuickStatusChange = (id: string, newStatus: RepairJobStatus) => {
    statusUpdateMutation.mutate(
      { id, status: newStatus },
      {
        onSuccess: (res) => {
          if (res?.success) {
            message.success(`Status updated to ${newStatus.replace("_", " ")}`);
            refetch();
          } else {
            message.error(res?.message || "Failed to update status.");
          }
        },
        onError: () => {
          message.error("Failed to update status.");
        },
      },
    );
  };

  const handleDeleteJob = (id: string) => {
    Modal.confirm({
      title: "Are you sure?",
      content: "This action will delete the repair job.",
      okText: "Delete",
      okButtonProps: { danger: true },
      cancelText: "Cancel",
      onOk: () => {
        deleteMutation.mutate(id, {
          onSuccess: (res) => {
            if (res?.success) {
              message.success("Repair job deleted successfully.");
            } else {
              message.error(res?.message || "Failed to delete repair job.");
            }
          },
          onError: () => {
            message.error("Failed to delete repair job.");
          },
        });
      },
    });
  };

  const columns = [
    {
      title: "Job #",
      dataIndex: "job_number",
      key: "job_number",
      render: (v: string) => <span className="font-semibold">{v}</span>,
    },
    {
      title: "Customer",
      key: "customer",
      render: (_: any, rec: RepairJob) => (
        <div>
          <div className="font-medium text-gray-800">
            {rec.customer_name || rec.temp_customer_name}
          </div>
          <div className="text-xs text-gray-400">
            {rec.customer_phone || rec.temp_customer_phone}
          </div>
        </div>
      ),
    },
    {
      title: "Device",
      key: "device",
      render: (_: any, rec: RepairJob) => (
        <div>
          <div className="font-medium text-gray-800">{rec.device_type}</div>
          <div className="text-xs text-gray-400">
            {[rec.device_brand, rec.device_model].filter(Boolean).join(" ")}
          </div>
        </div>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (v: RepairJobStatus, rec: RepairJob) => (
        <div className="flex items-center gap-1">
          <Tag color={statusColors[v]}>
            {v.replace("_", " ").toUpperCase()}
          </Tag>
          {isAllowed(RepairJobAction.UpdateStatus) && (
            <Select
              size="small"
              value={undefined}
              placeholder={<SwapOutlined className="text-xs text-gray-400" />}
              onChange={(val) => handleQuickStatusChange(rec.id, val)}
              className="w-7"
              dropdownMatchSelectWidth={140}
              style={{ minWidth: 28 }}
              bordered={false}
              loading={statusUpdateMutation.isPending}
              disabled={rec.status === "completed"}
            >
              <Option value="pending">Pending</Option>
              {rec.status === "completed" && (
                <Option value="completed" disabled>Completed</Option>
              )}
              <Option value="cancelled">Cancelled</Option>
            </Select>
          )}
        </div>
      ),
    },
    {
      title: "Priority",
      dataIndex: "priority",
      key: "priority",
      render: (v: RepairJobPriority) => (
        <Tag color={priorityColors[v]}>{v.toUpperCase()}</Tag>
      ),
    },
    {
      title: "Selling Price",
      dataIndex: "selling_price",
      key: "selling_price",
      render: (v: number) => `Rs. ${Number(v || 0).toFixed(2)}`,
    },
    {
      title: "Total Cost",
      dataIndex: "total_cost",
      key: "total_cost",
      render: (v: number) => `Rs. ${Number(v || 0).toFixed(2)}`,
    },
    {
      title: "Due Amount",
      dataIndex: "due_amount",
      key: "due_amount",
      render: (v: number) => (
        <span className={Number(v) > 0 ? "text-red-600 font-medium" : ""}>
          Rs. {Number(v).toFixed(2)}
        </span>
      ),
    },
    {
      title: "Received",
      dataIndex: "received_date",
      key: "received_date",
      render: (v: string) =>
        v ? new Date(v).toLocaleDateString() : "-",
    },
    {
      title: "Actions",
      key: "actions",
      width: 70,
      render: (_: any, rec: RepairJob) => {
        const statusOptions: MenuProps["items"] = [
          { key: "pending", label: "Pending", onClick: () => handleQuickStatusChange(rec.id, "pending") },
          ...(rec.status === "completed" ? [
            { key: "completed", label: "Completed", disabled: true }
          ] : []),
          { key: "cancelled", label: "Cancelled", onClick: () => handleQuickStatusChange(rec.id, "cancelled") },
        ];

        const menuItems: MenuProps["items"] = [
          {
            key: "view",
            label: "View Details",
            icon: <EyeOutlined />,
            onClick: () => handleViewJob(rec),
          },
          ...(isAllowed(RepairJobAction.Edit) ? [
            {
              key: "edit",
              label: "Edit Job",
              icon: <EditOutlined />,
              onClick: () => openDrawer(rec),
            },
          ] : []),
          ...(Number(rec.due_amount) > 0 && rec.status !== "completed" && rec.status !== "cancelled" ? [
            { type: "divider" as const },
            {
              key: "pay",
              label: "Proceed to Pay",
              icon: <DollarOutlined />,
              onClick: () => setPaymentJob(rec),
            },
            { type: "divider" as const },
          ] : []),
          ...(isAllowed(RepairJobAction.UpdateStatus) ? [
            {
              key: "status",
              label: "Change Status",
              icon: <SwapOutlined />,
              popupOffset: [0, -8],
              children: statusOptions,
              disabled: rec.status === "completed",
            },
          ] : []),
          { type: "divider" },
          ...(isAllowed(RepairJobAction.Delete) ? [
            {
              key: "delete",
              label: "Delete Job",
              icon: <DeleteOutlined />,
              danger: true,
              onClick: () => handleDeleteJob(rec.id),
            },
          ] : []),
        ];
        return (
          <Dropdown
            menu={{ items: menuItems }}
            trigger={["click"]}
            placement="bottomRight"
          >
            <Button
              type="text"
              size="small"
              icon={<MoreOutlined />}
              className="text-gray-400"
            />
          </Dropdown>
        );
      },
    },
  ];

  return (
    <div className="p-6 min-h-screen bg-gray-50">
      <PageHeader
        title="Service & Repair Jobs"
        subText={`Total repair jobs: ${total}`}
        handleButtons={[
          ...(isAllowed(RepairJobAction.Create) ? [
            {
              title: "New Repair Job",
              icon: PlusOutlined,
              onClick: () => openDrawer(),
            },
          ] : []),
          {
            title: "",
            icon: ReloadOutlined,
            onClick: refetch,
          },
        ]}
        searchBar={
          <div className="flex flex-wrap items-center gap-2 w-full sm:w-auto">
            <Select
              allowClear
              placeholder="Filter Status"
              onChange={(val) => {
                setSelectedStatus((val || "") as RepairJobStatus | "");
                setPage(1);
              }}
              className="w-full sm:w-36"
            >
              <Option value="pending">Pending</Option>
              <Option value="diagnosed">Diagnosed</Option>
              <Option value="in_progress">In Progress</Option>
              <Option value="completed">Completed</Option>
              <Option value="delivered">Delivered</Option>
              <Option value="cancelled">Cancelled</Option>
            </Select>

            <Select
              allowClear
              placeholder="Filter Priority"
              onChange={(val) => {
                setSelectedPriority((val || "") as RepairJobPriority | "");
                setPage(1);
              }}
              className="w-full sm:w-36"
            >
              <Option value="low">Low</Option>
              <Option value="normal">Normal</Option>
              <Option value="high">High</Option>
              <Option value="urgent">Urgent</Option>
            </Select>

            <Input
              placeholder="Search by job#, customer, device..."
              prefix={<SearchOutlined />}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onPressEnter={() => setPage(1)}
              allowClear
              onClear={() => {
                setSearchTerm("");
                setPage(1);
              }}
              className="w-full sm:w-56"
            />
          </div>
        }
      />

      <div className="mt-4 bg-white rounded-lg border border-gray-200 overflow-hidden shadow-sm">
        <Table
          dataSource={jobs}
          columns={columns}
          rowKey="id"
          loading={isLoading}
          pagination={tablePagination({
            current: page,
            pageSize: PAGE_SIZE,
            total,
            onChange: setPage,
            itemName: "repair jobs",
          })}
          scroll={{ x: 900 }}
          locale={{
            emptyText: (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description="No repair jobs found"
              />
            ),
          }}
        />
      </div>

      <RepairJobFormDrawer
        refetch={refetch}
        open={drawerOpen}
        onClose={closeDrawer}
        job={selectedJob}
        onSuccess={closeDrawer}
      />

      {viewJob && (
        <RepairJobViewDrawer
          open={viewDrawerOpen}
          onClose={() => {
            setViewDrawerOpen(false);
            setViewJob(null);
          }}
          job={viewJob}
        />
      )}

      {paymentJob && (
        <RepairJobPaymentModal
          open={!!paymentJob}
          onClose={() => setPaymentJob(null)}
          job={paymentJob}
        />
      )}
    </div>
  );
};
